import { Box, Collapse, Flex, HStack, useMediaQuery, VStack } from '@televet/kibble-ui/build/chakra';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import debounce from 'lodash-es/debounce';
import isEqual from 'lodash-es/isEqual';
import mapValues from 'lodash-es/mapValues';
import omit from 'lodash-es/omit';
import { useConversationsContext } from 'pages/Conversations/context/ConversationsContext';
import { ChannelSearchInputKeys, ChannelSearchInputUpdates } from 'pages/Conversations/interfaces/ChannelSearchInput';
import { defaultChannelListFilter } from 'pages/Conversations/interfaces/IChannelListFilter';
import { getConversationListWidth } from 'pages/Conversations/utils/styles';
import React, { KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConversationsList } from 'shared/components/ConversationsList';
import { DeviceSizes } from 'shared/enums/DeviceSizes';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ConversationEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useInfiniteScrollFetch } from '../../../../shared/hooks/useInfiniteScrollFetch';
import ChannelListItem from './ChannelListItem';
import { ChannelSearchList } from './ChannelListToolbar/components/ChannelSearchList';

export const DEFAULT_CHANNEL_LIST_PAGE_SIZE = 20;
export const DEFAULT_PINNED_CHANNEL_LIST_PAGE_SIZE = 100;

const ChannelList = (): JSX.Element => {
  const {
    channelListFilter,
    showEmptyState,
    clearChannelFilter,
    searchResultsCount,
    isSearching,
    channelSearchInput,
    hasNextPage,
    refetchChannels,
    hasLoadedInitialChannelList,
    isLoadingChannelList,
    showUnreadEmptyState,
    handleChannelSelect,
    unreadChannelIds,
    unreadMessageCountByChannel,
    unpinnedChannels,
    pinnedChannels,
    channelId,
    isFilterApplied,
    showSearchResultsCount,
    handleChannelSearchInputChange: onChannelSearchInputChange,
    handleSearchToggle: onSearchToggle,
    handleShowArchivedToggle: onShowArchivedToggle,
    currentChannelFilterSelection,
  } = useConversationsContext();
  const { currentClinicId } = useClinicUser();
  const { trackEvent } = useTracking();

  const [searchTextPetParentNames, setSearchTextPetParentNames] = useState('');
  const [searchTextPetNames, setSearchTextPetNames] = useState('');
  const [searchTextMessageBodies, setSearchTextMessageBodies] = useState('');
  const [isTouched, setIsTouched] = useState(false);

  const [isTablet, isDesktop] = useMediaQuery([`(${DeviceSizes.TabletMinWidth})`, `(${DeviceSizes.DesktopMinWidth})`]);
  const [isPinnedSectionExpanded, setIsPinnedSectionExpanded] = useState(true);

  const channelListContainerRef = useRef<HTMLDivElement | null>(null);

  /**
   * When a filter changes, reset to the first page and scroll to the top
   */
  useEffect(() => {
    if (channelListContainerRef.current) {
      channelListContainerRef.current.scrollTop = 0;
    }
  }, [channelSearchInput, channelListFilter, channelListContainerRef]);

  const getNextPage = useCallback(() => {
    if (hasNextPage) {
      refetchChannels({ take: DEFAULT_CHANNEL_LIST_PAGE_SIZE, skip: unpinnedChannels.length });
    }
  }, [unpinnedChannels.length, hasNextPage, refetchChannels]);

  const { isLoadingNextPage, listEndRef: channelListEndRef } = useInfiniteScrollFetch({
    containerRef: channelListContainerRef,
    hasInitialLoadCompleted: hasLoadedInitialChannelList,
    isLoading: isLoadingChannelList,
    fetchNextPage: getNextPage,
  });

  const containerWidth = useMemo(() => getConversationListWidth({ isDesktop, isTablet }), [isDesktop, isTablet]);

  const searchTextClientNameInputRef = useRef<HTMLInputElement | null>(null);
  const searchTextClientNameInputRefCallback = useCallback((element: HTMLInputElement | null) => {
    searchTextClientNameInputRef.current = element;
    element?.focus();
  }, []);

  const handleChannelSearchInputChange = useCallback(
    (key: ChannelSearchInputKeys, event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (key === ChannelSearchInputKeys.PetParentNames) setSearchTextPetParentNames(value);
      if (key === ChannelSearchInputKeys.PetNames) setSearchTextPetNames(value);
      if (key === ChannelSearchInputKeys.MessageBodies) setSearchTextMessageBodies(value);
    },
    [],
  );

  // eslint-disable-next-line
  const searchChannels = useCallback<
    ((updates: ChannelSearchInputUpdates) => void) & { flush: () => void | undefined }
  >(
    debounce(
      (updates: ChannelSearchInputUpdates): void => {
        onChannelSearchInputChange(mapValues(updates, (value) => value?.trim()));
      },
      340,
      { leading: false, trailing: true },
    ),
    [currentClinicId],
  );

  const handleSearchInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!isTouched) setIsTouched(true);
      if (event.key === 'Escape' || event.key === 'Esc') {
        onSearchToggle(false);
      } else if (event.key === 'Enter') {
        searchChannels({
          [ChannelSearchInputKeys.PetParentNames]: searchTextPetParentNames,
          [ChannelSearchInputKeys.PetNames]: searchTextPetNames,
          [ChannelSearchInputKeys.MessageBodies]: searchTextMessageBodies,
        });
        searchChannels.flush();
      }
    },
    [isTouched, onSearchToggle, searchChannels, searchTextPetParentNames, searchTextPetNames, searchTextMessageBodies],
  );

  const isNonArchivedFilterApplied = useMemo(() => {
    return !isEqual(
      omit(currentChannelFilterSelection.filters, 'showArchived'),
      omit(defaultChannelListFilter, 'showArchived'),
    );
  }, [currentChannelFilterSelection.filters]);

  const currentFilter = currentChannelFilterSelection.filters;

  const handleShowArchivedToggle = useCallback(() => {
    trackEvent(ConversationEvents.FilterShowArchivedToggled, {
      value: !currentChannelFilterSelection.filters.showArchived,
      source: 'conversations list search results',
    });
    onShowArchivedToggle();
  }, [currentChannelFilterSelection, onShowArchivedToggle, trackEvent]);

  const handleSearchInput = useCallback(
    (updates: ChannelSearchInputUpdates): void => {
      if (isSearching && isTouched) searchChannels(updates);
    },
    [isSearching, isTouched, searchChannels],
  );

  useEffect(() => {
    if (!isSearching) {
      setSearchTextPetParentNames('');
      setSearchTextPetNames('');
      setSearchTextMessageBodies('');
      setIsTouched(false);
    }
  }, [isSearching]);

  useEffect(() => {
    handleSearchInput({
      [ChannelSearchInputKeys.PetParentNames]: searchTextPetParentNames,
      [ChannelSearchInputKeys.PetNames]: searchTextPetNames,
      [ChannelSearchInputKeys.MessageBodies]: searchTextMessageBodies,
    });
  }, [searchTextPetParentNames, searchTextPetNames, searchTextMessageBodies, handleSearchInput]);

  return (
    <Flex className="ConversationsList" flex="0 0 auto" direction="column" w={containerWidth} px={2} pt={2}>
      <Box>
        <Collapse in={isSearching}>
          <Box borderWidth="1px" borderBottomColor="border.default" backgroundColor="white" borderRadius="md" mb={2}>
            <ChannelSearchList
              searchTextMessageBodies={searchTextMessageBodies}
              searchTextPetNames={searchTextPetNames}
              searchTextPetParentNames={searchTextPetParentNames}
              showArchived={currentFilter.showArchived}
              showSearchResultsCount={showSearchResultsCount}
              searchResultsCount={searchResultsCount}
              isFilterApplied={isNonArchivedFilterApplied}
              onClose={(): void => onSearchToggle(false)}
              searchTextClientNameInputRefCallback={searchTextClientNameInputRefCallback}
              onSearchInputChange={handleChannelSearchInputChange}
              onShowArchivedToggle={handleShowArchivedToggle}
              onSearchInputKeyDown={handleSearchInputKeyDown}
              onRemoveFilters={clearChannelFilter}
            />
          </Box>
        </Collapse>
      </Box>
      <ConversationsList
        containerRef={channelListContainerRef}
        listEndRef={channelListEndRef}
        hasNextPage={hasNextPage}
        showEmptyState={showEmptyState}
        showUnreadEmptyState={showUnreadEmptyState}
        onViewAllConversationsClick={(): void => clearChannelFilter('unread-empty-state')}
      >
        {!!pinnedChannels?.length && (
          <VStack align="flex-start">
            <HStack pb={2}>
              <Icon name="pin" size="sm" variant="subtle" />
              <Text size="sm" variant="subtle">
                Pinned Conversations {`(${pinnedChannels.length})`}
              </Text>
              <Button
                onClick={(): void => setIsPinnedSectionExpanded((prevState) => !prevState)}
                iconName={isPinnedSectionExpanded ? 'chevronDown' : 'chevronUp'}
                size="xs"
                variant="ghostNeutral"
              />
            </HStack>
            <Collapse in={isPinnedSectionExpanded} style={{ overflow: 'visible', width: '100%' }}>
              <VStack align="flex-start">
                {pinnedChannels.map((channel) => (
                  <ChannelListItem
                    key={channel.id}
                    channel={channel}
                    isCurrentChannel={!!channelId && channel.id === channelId}
                    isChannelPinned={true}
                    isLoadingNextPage={isLoadingNextPage}
                    hasUnreadMessages={
                      unreadChannelIds?.includes(channel.id) && unreadMessageCountByChannel[channel.id]?.count > 0
                    }
                    onChannelSelect={handleChannelSelect}
                  />
                ))}
              </VStack>
            </Collapse>
          </VStack>
        )}
        <VStack align="flex-start">
          <HStack py={2}>
            <Icon name="messages" size="sm" variant="subtle" />
            <Text size="sm" variant="subtle">
              {isFilterApplied || (isSearching && !!channelSearchInput)
                ? 'Filtered Conversations'
                : 'All Conversations'}{' '}
              {`(${searchResultsCount})`}
            </Text>
          </HStack>
          {!!searchResultsCount &&
            unpinnedChannels.map((channel) => (
              <ChannelListItem
                key={channel.id}
                channel={channel}
                isCurrentChannel={!!channelId && channel.id === channelId}
                isChannelPinned={false}
                isLoadingNextPage={isLoadingNextPage}
                hasUnreadMessages={
                  unreadChannelIds
                    ? unreadChannelIds.includes(channel.id) && unreadMessageCountByChannel[channel.id]?.count > 0
                    : false
                }
                onChannelSelect={handleChannelSelect}
              />
            ))}
        </VStack>
      </ConversationsList>
    </Flex>
  );
};

export default ChannelList;
